import { GetAppConfigQuery, PaymentAccountType, PointsStrategy, PointExpirationStrategy } from '@ocx/graphql';
import { logInDev } from '@ocx/utils';
import { LoyaltyCurrencyConfig } from '@ocx/feat-loyalty-currency';
import { mapSupportConfigurationFragmentToConfig } from '../sub-configurations/support/support-configuration.dto';
import { AssetKey, LocalRetailerConfiguration, RemoteConfiguration, RuntimeConfig } from '../app-config.types';
import { mapPaymentProviderConfigurationFragment } from './mapPaymentProviderConfigurationFragment';
import { mapOrderAheadConfigurationProviderFragment } from './mapOrderAheadConfigurationProviderFragment';
import * as appConfigConstants from '../app-config.constants';
import { loyaltyCurrencyAssets } from '../app-config.assets';

const buildGooglePayConfig = (remoteConfig: RemoteConfiguration): RuntimeConfig['googlePay'] => {
  if (!remoteConfig.googlePay) {
    logInDev('[Payments]: GooglePay is not configured');
    return null;
  }

  return {
    environment: remoteConfig.googlePay.environment,
    gateway: remoteConfig.googlePay.gateway,
    merchantId: remoteConfig.googlePay.merchantId,
    merchantName: remoteConfig.googlePay.merchantName,
    allowedCardNetworks: remoteConfig.googlePay.allowedCardNetworks,
    allowPrepaidCards: remoteConfig.googlePay.allowPrepaidCards,
    allowCreditCards: remoteConfig.googlePay.allowCreditCards,
    gatewayMerchantId: remoteConfig.googlePay.gatewayMerchantId,
  };
};

const buildApplePayConfig = (remoteConfig: RemoteConfiguration): RuntimeConfig['applePay'] => {
  if (!remoteConfig.applePay) {
    logInDev('[Payments]: ApplePay is not configured');
    return null;
  }

  return {
    merchantId: remoteConfig.applePay.merchantId,
    supportedNetworks: remoteConfig.applePay.supportedNetworks,
    allowCreditCards: remoteConfig.applePay.allowCreditCards,
    merchantName: remoteConfig.applePay.merchantName,
    allowPrepaidCards: remoteConfig.applePay.allowPrepaidCards,
  };
};

const buildPaymentProviderConfig = (remoteConfig: RemoteConfiguration): RuntimeConfig['paymentProvider'] => {
  if (!remoteConfig.paymentProvider) {
    logInDev('[Payments]: Payment Provider is not configured');
    return null;
  }

  const config = mapPaymentProviderConfigurationFragment(remoteConfig.paymentProvider);
  logInDev(`[Payments]: ${config?.type} is used`);
  return config;
};

const buildKountConfig = (remoteConfig: RemoteConfiguration): RuntimeConfig['kount'] => {
  if (!remoteConfig.kount) {
    logInDev(`[Payments]: Kount is not enabled`);
    return null;
  }

  logInDev(`[Payments]: Kount is up and running using ${remoteConfig.kount.environment} environment`);

  return {
    merchantId: remoteConfig.kount.merchantId,
    collectorHostname: remoteConfig.kount.collectorHostname,
    environment: remoteConfig.kount.environment,
  };
};

const buildStoreLocatorConfig = (remoteConfig: RemoteConfiguration): RuntimeConfig['storeLocator'] => {
  return {
    fuelPricesMode: remoteConfig.storeLocator.fuelPricesMode,
    googleMapsApiKey: remoteConfig.storeLocator.googleMapsApiKey,
    defaultLocation: {
      lat: remoteConfig.storeLocator.defaultLocation.latitude,
      lng: remoteConfig.storeLocator.defaultLocation.longitude,
    },
    watchTimeout: appConfigConstants.STORE_LOCATOR_WATCH_TIMEOUT,
    atTheStoreDistance: appConfigConstants.STORE_LOCATOR_AT_THE_STORE_DISTANCE,
  };
};

const buildPinCodeConfig = (remoteConfig: RemoteConfiguration): RuntimeConfig['pinCode'] => {
  return remoteConfig.pinCode
    ? {
        blockingDuration: remoteConfig.pinCode.blockingDuration,
        attemptThrottle: remoteConfig.pinCode.attemptThrottle,
        maxAttempts: remoteConfig.pinCode.maxAttempts,
        pinInMemoryTtl: appConfigConstants.PIN_IN_MEMORY_TTL,
      }
    : {
        pinInMemoryTtl: appConfigConstants.PIN_IN_MEMORY_TTL,
      };
};

const buildLocalOffersConfig = (remoteConfig: RemoteConfiguration): RuntimeConfig['localOffers'] => {
  return remoteConfig.localOffers
    ? {
        enabled: remoteConfig.localOffers.enabled,
        radiusInMeters: remoteConfig.localOffers.radiusInMeters,
      }
    : { enabled: false };
};

const buildSpireEnrollmentConfig = (remoteConfig: RemoteConfiguration): RuntimeConfig['spireEnrollment'] => {
  return remoteConfig.spireEnrollment?.enabled
    ? {
        enabled: remoteConfig.spireEnrollment.enabled,
        iframeUrl: remoteConfig.spireEnrollment.iframeUrl,
        partnerId: remoteConfig.spireEnrollment.partnerId,
      }
    : { enabled: false };
};

export const buildLoyaltyCurrencyConfig = (pointsStrategy: PointsStrategy): LoyaltyCurrencyConfig => {
  if (pointsStrategy === PointsStrategy.NotUsed) {
    return {
      pointsStrategy: PointsStrategy.NotUsed,
      iconConfig: null,
    };
  }

  const assetConfig = loyaltyCurrencyAssets[pointsStrategy];

  return {
    pointsStrategy,
    iconConfig: assetConfig,
  };
};

type BuildConfigParams = {
  remoteConfig: GetAppConfigQuery['appConfig'];
  envConfig: ISchemaConfig;
  localRetailerConfig: LocalRetailerConfiguration;
  assets: Record<AssetKey, string>;
};
export const buildRuntimeConfig = ({
  remoteConfig,
  envConfig,
  localRetailerConfig,
  assets,
}: BuildConfigParams): RuntimeConfig => {
  return {
    release: envConfig.release,
    build: envConfig.build,
    version: `${envConfig.release}.${envConfig.build}`,
    retailer: envConfig.retailer,
    chainId: remoteConfig.chainId,
    brandUuid: remoteConfig.brandUuid,
    subBrandName: envConfig.subBrandName,
    subBrandCode: envConfig.subBrandCode,

    termsConditions: {
      uuid: remoteConfig.termsConditions.uuid,
      name: remoteConfig.termsConditions.name,
      url: remoteConfig.termsConditions.url,
      activeAt: remoteConfig.termsConditions.activeAt,
    },
    privacyPolicy: {
      uuid: remoteConfig.privacyPolicy.uuid,
      name: remoteConfig.privacyPolicy.name,
      url: remoteConfig.privacyPolicy.url,
      activeAt: remoteConfig.privacyPolicy.activeAt,
    },
    support: mapSupportConfigurationFragmentToConfig(remoteConfig.support),

    rudderStack: {
      writeKey: envConfig.rudderStack.writeKey,
      dataPlaneUrl: envConfig.rudderStack.dataPlaneUrl,
      enabled: remoteConfig.rudderStack.enabled,
    },

    totalAvailableRollback: {
      enabled: remoteConfig.totalAvailableRollback.enabled,
    },

    marketing: {
      title: envConfig.marketing.title,
    },
    storeLocator: buildStoreLocatorConfig(remoteConfig),

    wallet: {
      enabled: remoteConfig.wallet.enabled,
      sizeLimit: remoteConfig.paymentWallet.maxInstruments,
      prepaidAccountTypeEnabled: remoteConfig.paymentWallet.allowedAccountTypes.includes(PaymentAccountType.Prepaid),
    },
    pinCode: buildPinCodeConfig(remoteConfig),
    paymentProvider: buildPaymentProviderConfig(remoteConfig),
    applePay: buildApplePayConfig(remoteConfig),
    googlePay: buildGooglePayConfig(remoteConfig),
    kount: buildKountConfig(remoteConfig),
    transaction: {
      pollInterval: appConfigConstants.TRANSACTION_POLL_INTERVAL,
      errorLimit: appConfigConstants.TRANSACTION_ERROR_LIMIT,
    },
    payAtPump: {
      enabled: remoteConfig.payAtPump.enabled,
    },
    payInside: {
      enabled: remoteConfig.payInside.enabled,
    },

    loyaltyProgram: {
      redeemOfferWithCompletedProfileOnly: remoteConfig.loyaltyProgram.redeemOfferWithCompletedProfileOnly,
      pointsStrategy: remoteConfig.loyaltyProgram.pointsStrategy,
      pointExpiration: {
        ...remoteConfig.loyaltyProgram.pointExpiration,
        isFifo: remoteConfig.loyaltyProgram.pointExpiration.strategy === PointExpirationStrategy.FirstInFirstOut,
        isActivityBased: remoteConfig.loyaltyProgram.pointExpiration.strategy === PointExpirationStrategy.ActivityBased,
        isNone: remoteConfig.loyaltyProgram.pointExpiration.strategy === PointExpirationStrategy.None,
      },
      homePageOffersLimit: remoteConfig.loyaltyProgram.homePageOffersLimit,
      membershipMinAge: remoteConfig.loyaltyProgram.membershipMinAge,
      membershipMaxAge: appConfigConstants.MEMBERSHIP_MAX_AGE,
      isExplicitTermsAcceptanceRequired: remoteConfig.loyaltyProgram.isExplicitTermsAcceptanceRequired,
      isExplicitSmsPromotionAcceptanceEnabled: remoteConfig.loyaltyProgram.isExplicitSmsPromotionAcceptanceEnabled,
      isPromocodesEnabled: remoteConfig.loyaltyProgram.isPromocodesEnabled,
      referralProgramConfig: remoteConfig.loyaltyProgram.referralProgramConfig,
    },
    loyaltyCurrency: buildLoyaltyCurrencyConfig(remoteConfig.loyaltyProgram.pointsStrategy),
    localRetailerConfig,

    deleteAccount: {
      enabled: remoteConfig.deleteAccount.enabled,
      deleteAccountMutation: remoteConfig.deleteAccount.deleteAccountMutation,
    },
    communicationPreference: {
      enabled: remoteConfig.communicationPreference.enabled,
      isSmsOptInConfigurable: remoteConfig.communicationPreference.isSmsOptInConfigurable,
      isPushOptInConfigurable: remoteConfig.communicationPreference.isPushOptInConfigurable,
    },
    eaivAgeVerification: remoteConfig.eaivAgeVerification,
    eaivIdentityVerification: remoteConfig.eaivIdentityVerification,
    productInterests: remoteConfig.productInterests,
    appRating: remoteConfig.appRating,
    auth: {
      primaryMethod: remoteConfig.auth.primaryMethod,
      otp: {
        enabled: remoteConfig.auth.otp.enabled && remoteConfig.auth.otp.channels.length > 0,
        channels: remoteConfig.auth.otp.channels,
      },
    },
    smsOptIn: {
      enabled: remoteConfig.smsOptIn.enabled,
      modalAppearanceDelaySeconds: remoteConfig.smsOptIn.modalAppearanceDelaySeconds,
      modalOfferLoyaltyEventName: remoteConfig.smsOptIn.modalOfferLoyaltyEventName,
      optInAcceptedLoyaltyEventName: remoteConfig.smsOptIn.optInAcceptedLoyaltyEventName,
      optInRejectedLoyaltyEventName: remoteConfig.smsOptIn.optInRejectedLoyaltyEventName,
    },
    mobilePaymentsReceiptsView: {
      enabled: remoteConfig.mobilePaymentsReceiptsView.enabled,
    },
    smsOptedOutWarning: {
      enabled: remoteConfig.smsOptedOutWarning.enabled,
      smsOptInKeyword: remoteConfig.smsOptedOutWarning.smsOptInKeyword,
      smsOptInPhoneNumber: remoteConfig.smsOptedOutWarning.smsOptInPhoneNumber,
    },
    orderAhead: !remoteConfig.orderAhead?.enabled
      ? { enabled: false }
      : {
          enabled: remoteConfig.orderAhead.enabled,
          navbar: {
            icon: remoteConfig.orderAhead.navbar.icon,
            text: remoteConfig.orderAhead.navbar.text,
          },
          provider: mapOrderAheadConfigurationProviderFragment(remoteConfig.orderAhead.provider as any),
        },
    externalIdentityVerification: !remoteConfig.externalIdentityVerification?.enabled
      ? { enabled: false }
      : { enabled: true, provider: remoteConfig.externalIdentityVerification.provider },
    personalDataShareOptOut: !remoteConfig.personalDataShareOptOut?.enabled
      ? { enabled: false }
      : { enabled: true, optOutUrl: remoteConfig.personalDataShareOptOut.optOutUrl },
    appsFlyer: !remoteConfig.appsFlyer?.enabled
      ? { enabled: false }
      : {
          enabled: remoteConfig.appsFlyer.enabled,
          devKey: remoteConfig.appsFlyer.devKey,
          appId: remoteConfig.appsFlyer.appId,
          webDevKey: remoteConfig.appsFlyer.webDevKey,
        },
    localOffers: buildLocalOffersConfig(remoteConfig),
    spireEnrollment: buildSpireEnrollmentConfig(remoteConfig),
    loyaltyIdQRCode: {
      enabled: remoteConfig.loyaltyIdQRCode?.enabled ?? false,
    },
    assets,
  };
};
